import { createRootRouteWithContext, createRoute, Outlet, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';

import { RootLayout } from './components/RootLayout.js';
import { LoginPage } from './routes/login.js';
import { AcceptInvitePage } from './routes/accept.js';
import { WorkspacesPage } from './routes/workspaces/index.js';
import { WorkspaceLayout } from './routes/workspaces/$id/layout.js';
import { WorkspaceOverview } from './routes/workspaces/$id/index.js';
import { ServicesPage } from './routes/workspaces/$id/services.js';
import { InsightsPage } from './routes/workspaces/$id/insights.js';
import { LogsPage } from './routes/workspaces/$id/logs.js';
import { BillingPage } from './routes/workspaces/$id/billing.js';
import { TeamPage } from './routes/workspaces/$id/team.js';
import { OperationsPage } from './routes/workspaces/$id/operations.js';
import { SettingsPage } from './routes/workspaces/$id/settings.js';
import { AiVisibilityPage } from './routes/workspaces/$id/ai-visibility.js';
import { AdminLayout } from './routes/admin/layout.js';
import { AdminInvitations } from './routes/admin/invitations.js';
import { AdminOnboarding } from './routes/admin/onboarding.js';
import { AdminPlans } from './routes/admin/plans.js';
import { AdminPartners } from './routes/admin/partners.js';
import { AdminTenants } from './routes/admin/tenants.js';
import { AdminAudit } from './routes/admin/audit.js';
import { NotFound } from './components/NotFound.js';

export interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <RootLayout>
      <Outlet />
    </RootLayout>
  ),
  notFoundComponent: NotFound,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const acceptRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept',
  component: AcceptInvitePage,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/workspaces' });
  },
  component: () => null,
});

const workspacesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workspaces',
  component: WorkspacesPage,
});

const workspaceLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workspaces/$id',
  component: WorkspaceLayout,
});

const workspaceIndex = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/',
  component: WorkspaceOverview,
});
const servicesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/services',
  component: ServicesPage,
});
const insightsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/insights',
  component: InsightsPage,
});
const logsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/logs',
  component: LogsPage,
});
const billingRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/billing',
  component: BillingPage,
});
const teamRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/team',
  component: TeamPage,
});
const operationsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/operations',
  component: OperationsPage,
});
const settingsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/settings',
  component: SettingsPage,
});
const aiVisibilityRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: '/ai-visibility',
  component: AiVisibilityPage,
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminLayout,
});
const adminInvitationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/invitations',
  component: AdminInvitations,
});
const adminOnboardingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/onboarding',
  component: AdminOnboarding,
});
const adminPlansRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/plans',
  component: AdminPlans,
});
const adminPartnersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/partners',
  component: AdminPartners,
});
const adminTenantsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/tenants',
  component: AdminTenants,
});
const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/audit',
  component: AdminAudit,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  acceptRoute,
  workspacesRoute,
  workspaceLayoutRoute.addChildren([
    workspaceIndex,
    servicesRoute,
    insightsRoute,
    logsRoute,
    billingRoute,
    teamRoute,
    operationsRoute,
    settingsRoute,
    aiVisibilityRoute,
  ]),
  adminRoute.addChildren([
    adminInvitationsRoute,
    adminOnboardingRoute,
    adminPlansRoute,
    adminPartnersRoute,
    adminTenantsRoute,
    adminAuditRoute,
  ]),
]);
